import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { WebSocketContext } from '@/contexts/WebSocketContext';
import { useRwdUI } from '@/hooks/useRwdUI';
import { CMDPsBetRecordQuery } from '@/models/cmd/live';
import { getBetSummaryReportState } from '@/modules/report/selector';
import {
    betSummarySliceActions,
    Summary,
} from '@/modules/report/slice/betSummary';
import { RwdTypography } from '@/modules/rwdGame/components/RwdTypography';
import { RootState } from '@/store/store';
import { AmountText } from '@/utils/formatter';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { SummaryDetail } from '../betRecordDetail';
import { SummaryGameRoundText } from './components/SummaryGameRoundText';
import './index.scss';

interface GroupedSummary {
    date: string;
    totalBetAmount: number;
    totalWinLoss: number;
    summaries: Summary[];
    open: boolean;
    isAllOnGoing: boolean;
}

interface GroupFlattenedItem {
    type: 'group';
    data: GroupedSummary;
    groupId: string;
}

interface ItemFlattenedItem {
    type: 'item';
    data: Summary;
    groupId: string;
}

type FlattenedItem = GroupFlattenedItem | ItemFlattenedItem;

const USE_AUTOSIZER = true;

type Props = {
    isSummaryExpand: boolean;
};

export const BetRecordSummary = (props: Props) => {
    const { isSummaryExpand = true } = props;

    const { t } = useTranslation();

    const { playButtonAudio } = useContext(AudioPlayContext);
    const { isLandscape, scaleFactor } = useContext(ScreenContext);
    const { sendCommand } = useContext(WebSocketContext);

    const { summaries, selectedFilterType } = useSelector((state: RootState) =>
        getBetSummaryReportState(state)
    );

    const [groupedSummaries, setGroupedSummaries] = useState<GroupedSummary[]>(
        []
    );
    const [selectedSummary, setSelectedSummary] = useState<Summary>();
    const { pxToRwdNumber } = useRwdUI();
    const listRef = useRef<VariableSizeList>(null);

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(betSummarySliceActions.updateSelectedFilterType(-1));
    }, []);

    const itemHeight = useMemo(() => {
        return pxToRwdNumber(isLandscape ? 75 : 48);
    }, [isLandscape, scaleFactor, pxToRwdNumber]);

    const groupHeaderHeight = useMemo(() => {
        return pxToRwdNumber(isLandscape ? 75 : 48);
    }, [isLandscape, scaleFactor, pxToRwdNumber]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [itemHeight, groupHeaderHeight]);

    const [levelIndex, setLevelIndex] = useState(-1);

    const onClickSummary = (summary: Summary) => {
        setSelectedSummary(summary);

        // reset bet records
        dispatch(
            betSummarySliceActions.updateBetRecord({
                successBetRecords: [],
                unsuccessBetRecords: [],
            })
        );
        const request = new CMDPsBetRecordQuery();
        request.GameID = summary.GameID;
        sendCommand(request);

        playButtonAudio();
    };

    useEffect(() => {
        if (summaries && summaries.length > 0) {
            const filterSummary = summaries.filter(
                s => (selectedFilterType & s.GameType) === s.GameType
            );

            const grouped = filterSummary.reduce((acc, summary) => {
                const date = summary.ReportDate.split(' ')[0];
                const existingGroup = acc.find(g => g.date === date);
                const isSummaryOnGoing =
                    summary.State === 0 || summary.WinSlot === 0;

                if (existingGroup) {
                    existingGroup.totalBetAmount += summary.BetAmount;
                    existingGroup.totalWinLoss += summary.ResultAmount;
                    existingGroup.summaries.push(summary);
                    existingGroup.isAllOnGoing =
                        existingGroup.isAllOnGoing && isSummaryOnGoing;
                } else {
                    acc.push({
                        date,
                        totalBetAmount: summary.BetAmount,
                        totalWinLoss: summary.ResultAmount,
                        summaries: [summary],
                        open: isSummaryExpand,
                        isAllOnGoing: isSummaryOnGoing,
                    });
                }

                return acc;
            }, [] as GroupedSummary[]);

            setGroupedSummaries(grouped);
        } else {
            setGroupedSummaries([]);
        }
    }, [summaries, selectedFilterType]);

    const toggleGroup = (date: string) => {
        setGroupedSummaries(prev =>
            prev.map(group =>
                group.date === date ? { ...group, open: !group.open } : group
            )
        );

        if (USE_AUTOSIZER) {
            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.resetAfterIndex(0);
                }
            }, 0);
        }
    };

    const flattenedItems = useMemo(() => {
        const items: FlattenedItem[] = [];

        groupedSummaries.forEach(group => {
            items.push({
                type: 'group',
                data: group,
                groupId: group.date,
            } as GroupFlattenedItem);

            if (group.open) {
                group.summaries.forEach(summary => {
                    items.push({
                        type: 'item',
                        data: summary,
                        groupId: group.date,
                    } as ItemFlattenedItem);
                });
            }
        });

        return items;
    }, [groupedSummaries]);

    const getItemSize = useMemo(() => {
        return (index: number) => {
            const item = flattenedItems[index];
            return item.type === 'group' ? groupHeaderHeight : itemHeight;
        };
    }, [flattenedItems, groupHeaderHeight, itemHeight]);

    const Row = ({
        index,
        style,
    }: {
        index: number;
        style?: React.CSSProperties;
    }) => {
        const item = flattenedItems[index];

        if (item.type === 'group') {
            return (
                <div style={style}>
                    <div
                        id="btn_set-br_summary"
                        className="summary-group-header"
                        onClick={() => toggleGroup(item.data.date)}
                        style={{
                            WebkitTapHighlightColor: 'transparent',
                            userSelect: 'none',
                            height: '100%',
                        }}
                    >
                        <SummaryGroup
                            date={item.data.date}
                            totalBetAmount={item.data.totalBetAmount}
                            totalWinLoss={item.data.totalWinLoss}
                            open={item.data.open}
                            isAllOnGoing={item.data.isAllOnGoing}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div style={style}>
                    <SummaryItem
                        onClick={() => {
                            setLevelIndex(0);
                            onClickSummary(item.data);
                        }}
                        info={item.data}
                        maxHeight={itemHeight}
                    />
                </div>
            );
        }
    };

    const renderListItems = () => {
        return flattenedItems.map(item => {
            if (item.type === 'group') {
                return (
                    <div
                        key={`group-${item.data.date}`}
                        style={{ height: groupHeaderHeight }}
                    >
                        <div
                            className="summary-group-header"
                            onClick={() => toggleGroup(item.data.date)}
                            style={{
                                WebkitTapHighlightColor: 'transparent',
                                userSelect: 'none',
                                height: '100%',
                            }}
                        >
                            <SummaryGroup
                                date={item.data.date}
                                totalBetAmount={item.data.totalBetAmount}
                                totalWinLoss={item.data.totalWinLoss}
                                open={item.data.open}
                                isAllOnGoing={item.data.isAllOnGoing}
                            />
                        </div>
                    </div>
                );
            } else {
                return (
                    <div
                        key={`item-${item.data.GameID}`}
                        style={{ height: itemHeight }}
                    >
                        <SummaryItem
                            onClick={() => {
                                setLevelIndex(0);
                                onClickSummary(item.data);
                            }}
                            info={item.data}
                            maxHeight={itemHeight}
                        />
                    </div>
                );
            }
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0, // Important for flex scrolling
            }}
        >
            <div className="bet-record-summary-header-wrapper">
                <Grid container className="bet-record-summary-header">
                    <Grid item className="bet-record-summary-header-item date">
                        <span>{t('betlog.date')}</span>
                    </Grid>
                    <Grid item className="bet-record-summary-header-item round">
                        <span>{t('betlog.game')}</span>
                    </Grid>
                    <Grid
                        item
                        className="bet-record-summary-header-item bet-amount"
                    >
                        {t('betlog.bet_amount')}
                    </Grid>
                    <Grid
                        item
                        className="bet-record-summary-header-item winlose"
                    >
                        {t('betlog.win_lose')}
                    </Grid>
                </Grid>
            </div>
            <div
                id="summary-list-viewport"
                className="summary-list-viewport"
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0,
                }}
            >
                {groupedSummaries.length > 0 ? (
                    USE_AUTOSIZER ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <VariableSizeList
                                    className="virtualized-list"
                                    ref={listRef}
                                    height={height}
                                    width={width}
                                    itemCount={flattenedItems.length}
                                    itemSize={getItemSize}
                                    itemKey={index => {
                                        const item = flattenedItems[index];
                                        if (item.type === 'group') {
                                            return `group-${item.data.date}`;
                                        } else {
                                            return `item-${item.data.GameID}`;
                                        }
                                    }}
                                    // overscanCount={5}
                                >
                                    {Row}
                                </VariableSizeList>
                            )}
                        </AutoSizer>
                    ) : (
                        <div style={{ height: '100%', overflow: 'auto' }}>
                            <List
                                className="bet-record-summary-list"
                                sx={{
                                    width: '100%',
                                    padding: 0,
                                    WebkitTapHighlightColor: 'transparent',
                                    userSelect: 'none',
                                }}
                            >
                                {renderListItems()}
                            </List>
                        </div>
                    )
                ) : (
                    <Grid item container className="bet-report-notice-message">
                        {t('betlog.noRecord')}
                    </Grid>
                )}
            </div>
            <Dialog
                open={levelIndex >= 0}
                onClose={() => {
                    setLevelIndex(-1);
                }}
                className="bet-record-report"
                disableEscapeKeyDown
            >
                <IconButton
                    disableRipple
                    onClick={() => {
                        setLevelIndex(-1);
                        playButtonAudio();
                    }}
                    className="close-report"
                >
                    <CloseIcon className="close-icon" />
                </IconButton>
                <div
                    id="report-title-place-holder"
                    className="title-place-holder"
                >
                    <div className="place-holder-left" />
                    <div className="place-holder-right" />
                </div>
                {selectedSummary && <SummaryDetail summary={selectedSummary} />}
            </Dialog>
        </Box>
    );
};

type SummaryGroupProps = {
    date: string;
    totalBetAmount: number;
    totalWinLoss: number;
    open: boolean;
    isAllOnGoing: boolean;
};

const SummaryGroup = (props: SummaryGroupProps) => {
    const { date, totalBetAmount, totalWinLoss, open } = props;

    const { isLandscape } = useContext(ScreenContext);

    return (
        <div className="bet-record-summary-item-layout-detector">
            <div className="bet-record-summary-item-layout-wrapper">
                <Grid container className="bet-record-summary-item-container">
                    {open ? (
                        <ExpandMore className="bet-record-summary-item arrow" />
                    ) : (
                        <NavigateNextIcon className="bet-record-summary-item arrow" />
                    )}
                    <Grid item className="bet-record-summary-item date">
                        <span>{date}</span>
                    </Grid>
                    <Grid item className="bet-record-summary-item bet-amount">
                        {totalBetAmount == 0 && (
                            <div className="unsuccessful">
                                <RwdTypography
                                    portraitFontSize={18}
                                    landscapeFontSize={36}
                                >
                                    -
                                </RwdTypography>
                            </div>
                        )}

                        {totalBetAmount > 0 && (
                            <AmountText
                                amount={totalBetAmount / 100}
                                textAlign={'end'}
                                with_color={false}
                                with_sign={false}
                                zero_white_no_sign={true}
                                disableAuto={false}
                                lineHeight={
                                    isLandscape
                                        ? 'calc(48px * var(--global-scale-factor))'
                                        : 'calc(24px * var(--global-scale-factor))'
                                }
                                isPortraitWithScaleFactor={true}
                            />
                        )}
                    </Grid>
                    <Grid item className="bet-record-summary-item winlose">
                        {totalBetAmount == 0 && (
                            <div className="unsuccessful">
                                <RwdTypography
                                    portraitFontSize={18}
                                    landscapeFontSize={36}
                                >
                                    -
                                </RwdTypography>
                            </div>
                        )}
                        {totalBetAmount > 0 && (
                            <AmountText
                                amount={totalWinLoss / 100}
                                textAlign={'end'}
                                with_color={true}
                                with_sign={true}
                                zero_white_no_sign={true}
                                disableAuto={false}
                                lineHeight={
                                    isLandscape
                                        ? 'calc(48px * var(--global-scale-factor))'
                                        : 'calc(24px * var(--global-scale-factor))'
                                }
                                isPortraitWithScaleFactor={true}
                            />
                        )}
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

type SummaryItemProps = {
    info: Summary;
    maxHeight: number;
    onClick: () => void;
};

const SummaryItem = (props: SummaryItemProps) => {
    const { info, maxHeight, onClick } = props;

    const { isLandscape } = useContext(ScreenContext);
    const [onGoing, setOnGoing] = useState<boolean>(false);

    useEffect(() => {
        setOnGoing(info.State == 0 || info.WinSlot == 0);
    }, [info]);

    return (
        <div className="bet-record-summary-item-layout-detector">
            <div
                id="btn_set-br_summary-date"
                className="bet-record-summary-item-layout-wrapper"
                onClick={onClick}
                style={{
                    WebkitTapHighlightColor: 'transparent',
                    userSelect: 'none',
                }}
            >
                <Grid
                    container
                    className="bet-record-summary-item-container"
                    sx={{ maxHeight: maxHeight }}
                >
                    <Grid item className="bet-record-summary-item date">
                        <span>{`${info.ReportDate.split(' ')[1]}`}</span>
                    </Grid>
                    <Grid item className="bet-record-summary-item round">
                        <SummaryGameRoundText summary={info} />
                    </Grid>
                    <Grid item className="bet-record-summary-item bet-amount">
                        {info.BetAmount == 0 && (
                            <div className="unsuccessful">
                                <RwdTypography
                                    portraitFontSize={18}
                                    landscapeFontSize={36}
                                >
                                    -
                                </RwdTypography>
                            </div>
                        )}
                        {info.BetAmount > 0 && (
                            <AmountText
                                amount={info.BetAmount / 100}
                                textAlign={'end'}
                                with_color={false}
                                with_sign={false}
                                zero_white_no_sign={true}
                                disableAuto={false}
                                lineHeight={
                                    isLandscape
                                        ? 'calc(48px * var(--global-scale-factor))'
                                        : 'calc(24px * var(--global-scale-factor))'
                                }
                                isPortraitWithScaleFactor={true}
                            />
                        )}
                    </Grid>
                    <Grid item className="bet-record-summary-item winlose">
                        {info.BetAmount == 0 && (
                            <div className="unsuccessful">
                                <RwdTypography
                                    portraitFontSize={18}
                                    landscapeFontSize={36}
                                >
                                    -
                                </RwdTypography>
                            </div>
                        )}
                        {info.BetAmount > 0 && onGoing && <></>}
                        {info.BetAmount > 0 && !onGoing && (
                            <AmountText
                                amount={info.ResultAmount / 100}
                                textAlign={'end'}
                                with_color={true}
                                with_sign={true}
                                zero_white_no_sign={true}
                                disableAuto={false}
                                lineHeight={
                                    isLandscape
                                        ? 'calc(48px * var(--global-scale-factor))'
                                        : 'calc(24px * var(--global-scale-factor))'
                                }
                                isPortraitWithScaleFactor={true}
                            />
                        )}
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default BetRecordSummary;
